import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import RichTextEditor from '../../../components/RichTextEditor';
import 'react-quill/dist/quill.snow.css';
import { cloudinaryConfig } from '../../../config/cloudinary';

interface HeroData {
  title: string;
  subtitle: string;
  ctaText: string;
  backgroundImage: string;
  heroImage: string;
  enabled: boolean;
}

const HeroEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [heroData, setHeroData] = useState<HeroData>({
    title: '',
    subtitle: '',
    ctaText: '',
    backgroundImage: '',
    heroImage: '',
    enabled: true,
  });

  useEffect(() => {
    loadHeroData();
  }, []);

  const loadHeroData = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'landingPageContent', 'hero');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setHeroData({
          title: data.title || '',
          subtitle: data.subtitle || '',
          ctaText: data.ctaText || '',
          backgroundImage: data.backgroundImage || '',
          heroImage: data.heroImage || '',
          enabled: data.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Error loading hero data:', error);
      showToast({
        message: 'Failed to load hero data',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setHeroData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setHeroData((prev) => ({ ...prev, [name]: checked }));
  };

  // Cloudinary upload function
  const uploadToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', 'landing-page/hero');

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    const data = await response.json();
    return data.secure_url;
  };

  const handleBackgroundImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast({
        message: 'Image size should be less than 5MB',
        type: 'error',
      });
      return;
    }

    setUploadingBg(true);
    try {
      const downloadURL = await uploadToCloudinary(file);

      setHeroData((prev) => ({ ...prev, backgroundImage: downloadURL }));
      showToast({
        message: 'Background image uploaded successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error uploading background image:', error);
      showToast({
        message: 'Failed to upload background image',
        type: 'error',
      });
    } finally {
      setUploadingBg(false);
    }
  };

  const handleHeroImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast({
        message: 'Image size should be less than 5MB',
        type: 'error',
      });
      return;
    }

    setUploadingHero(true);
    try {
      const downloadURL = await uploadToCloudinary(file);

      setHeroData((prev) => ({ ...prev, heroImage: downloadURL }));
      showToast({
        message: 'Hero image uploaded successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error uploading hero image:', error);
      showToast({
        message: 'Failed to upload hero image',
        type: 'error',
      });
    } finally {
      setUploadingHero(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landingPageContent', 'hero');
      await setDoc(docRef, {
        ...heroData,
        lastUpdated: serverTimestamp(),
      });

      showToast({
        message: 'Hero section updated successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error saving hero data:', error);
      showToast({
        message: 'Failed to save changes',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/cms')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Hero Section
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Main headline and call-to-action at the top of your landing page
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Title */}
      <RichTextEditor
        value={heroData.title}
        onChange={(value) => setHeroData((prev) => ({ ...prev, title: value }))}
        placeholder="e.g., Protect yourself from deepfake audio scams"
        label="Main Title"
        required
      />

      {/* Title Preview */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <p className="text-xs font-medium text-gray-500 mb-2">TITLE PREVIEW:</p>
        <div
          className="text-3xl font-bold leading-tight text-white bg-gray-800 p-4 rounded"
          dangerouslySetInnerHTML={{ __html: heroData.title }}
        />
      </div>

      {/* Subtitle */}
      <RichTextEditor
        value={heroData.subtitle}
        onChange={(value) =>
          setHeroData((prev) => ({ ...prev, subtitle: value }))
        }
        placeholder="e.g., Advanced AI detection of audio calls in real-time..."
        label="Subtitle"
        required
      />

      {/* Subtitle Preview */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <p className="text-xs font-medium text-gray-500 mb-2">
          SUBTITLE PREVIEW:
        </p>
        <div
          className="text-lg text-gray-600"
          dangerouslySetInnerHTML={{ __html: heroData.subtitle }}
        />
      </div>

      {/* CTA Button */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          CTA Button Text *
        </label>
        <input
          type="text"
          name="ctaText"
          value={heroData.ctaText}
          onChange={handleInputChange}
          placeholder="e.g., Get Unveil Now"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          required
        />
      </div>

      {/* Background Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Background Image
        </label>
        <div className="space-y-4">
          {heroData.backgroundImage && (
            <div className="relative w-full h-48 rounded-lg overflow-hidden">
              <img
                src={heroData.backgroundImage}
                alt="Hero background"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageUpload}
              disabled={uploadingBg}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark file:cursor-pointer disabled:opacity-50"
            />
            <p className="mt-1 text-sm text-gray-500">
              {uploadingBg
                ? 'Uploading to Cloudinary...'
                : 'Full-width background - Recommended: 1920x1080px, max 5MB'}
            </p>
          </div>
        </div>
      </div>

      {/* Hero Image (Phone Mockup) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Hero Image (Phone Mockup)
        </label>
        <div className="space-y-4">
          {heroData.heroImage && (
            <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
              <img
                src={heroData.heroImage}
                alt="Hero phone mockup"
                className="h-full object-contain"
              />
            </div>
          )}
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleHeroImageUpload}
              disabled={uploadingHero}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark file:cursor-pointer disabled:opacity-50"
            />
            <p className="mt-1 text-sm text-gray-500">
              {uploadingHero
                ? 'Uploading to Cloudinary...'
                : 'Phone mockup or product image - PNG with transparency recommended, max 5MB'}
            </p>
          </div>
        </div>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="enabled"
          name="enabled"
          checked={heroData.enabled}
          onChange={handleCheckboxChange}
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
        />
        <label htmlFor="enabled" className="ml-2 block text-sm text-gray-700">
          Enable this section
        </label>
      </div>
    </div>
  );
};

export default HeroEditorPage;
